import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography, Box } from '@mui/material';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useTranslations } from 'hooks/useTranslations';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { formatDate } from 'utils/date';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { getStatusVariant } from './helper';

interface ILicenceConfiguratorTableCard {
  data: ITableDataSource;
  dateFormat: string;
  handleView: (id: string) => void;
}

const LicenceConfiguratorTableCard = ({
  data,
  dateFormat,
  handleView,
}: ILicenceConfiguratorTableCard) => {
  const { t } = useTranslations();

  const duration = () => {
    if (data.licenceDurationUnlimited) {
      return (
        <Chips
          variant={EChipsVariants.success}
          text={t('licenceConfiguration.duration.unlimited')}
          fullWidth
        />
      );
    }

    const duration: string[] = [];

    if (data.licenceDurationYears) {
      duration.push(
        `${data.licenceDurationYears} ${
          data.licenceDurationYears > 1
            ? t('licenceConfiguration.duration.years')
            : t('licenceConfiguration.duration.year')
        }`,
      );
    }

    if (data.licenceDurationMonths) {
      duration.push(
        `${data.licenceDurationMonths} ${
          data.licenceDurationMonths > 1
            ? t('licenceConfiguration.duration.months')
            : t('licenceConfiguration.duration.month')
        }`,
      );
    }

    if (data.licenceDurationDays) {
      duration.push(
        `${data.licenceDurationDays} ${
          data.licenceDurationDays > 1
            ? t('licenceConfiguration.duration.days')
            : t('licenceConfiguration.duration.day')
        }`,
      );
    }

    return duration.join(', ');
  };

  return (
    <Grid
      container
      size={{ xs: 12 }}
      spacing={2}
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        mb: 2,
        p: 2,
        backgroundColor: colors.white,
      }}
    >
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licence.code.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.code)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licenceConfiguration.licenceName.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.name)}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licenceConfigurator.status.column')}
        </Typography>
        <Box>
          <Chips variant={getStatusVariant(data.statusId)} text={data.status} fullWidth />
        </Box>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licenceConfigurator.duration.column')}
        </Typography>
        <Typography variant="body2">{duration()}</Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licenceConfiguration.leType.column')}
        </Typography>
        <Typography variant="body2">
          {data?.legalTypes.map(
            (type: string, key: number) =>
              `${t(`licence.userType.${type.toLowerCase()}.label`)}${
                data?.legalTypes.length > key + 1 ? ', ' : ''
              } `,
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px', color: colors.gray500 }}>
          {t('licenceConfigurator.createDate.column')}
        </Typography>
        <Typography variant="body2">
          {data.createdDate ? formatDate(data.createdDate, dateFormat) : '-'}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
        <Button
          id={`licence_configuration-view-${data.id}`}
          label={t('licenceConfiguration.seeDetails.button')}
          variant={EButtonVariants.outlined}
          size={EButtonSizes.small}
          onClick={() => handleView(data.id as string)}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default LicenceConfiguratorTableCard;
